import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { userProfileOptions } from '#authFeature/models/userProfile/queries';
import FullscreenLoader from '#globalComponents/FullscreenLoader/FullscreenLoader';
import MaintenanceGate from '#globalComponents/MaintenanceGate/MaintenanceGate';
import { useEnsureOrg } from '#globalContexts/useEnsureOrg';
import useZendeskMessenger from '#globalHooks/useZendeskMessenger';
import { authClient } from '#lib/authClient';
import { clearCachedAuthToken, decodeCapabilities, decodePlatformClaims, getAccessToken } from '#lib/authToken';
import trackCompleteRegistration, { generateEventId } from '#lib/facebookConversionApi';
import { queryClient } from '#lib/queryClient';
import { setSentryUser, clearSentryUser } from '#lib/sentry';
import { logoutZendeskUser } from '#lib/zendesk';
import { PendingInvitesModal } from '#orgFeature/components/PendingInvitesModal/PendingInvitesModal';
import { orgInfoOptions, pendingInvitesOptions } from '#orgFeature/models/org/queries';
import { subscriptionOptions } from '#settingsFeature/models/billing/queries';

const AuthContext = createContext();

/**
 * Better Auth signup. Returns a { data, error } shape so the calling pages are unchanged.
 * The backend databaseHook creates the public.users profile; name is passed through.
 * @param {string} email
 * @param {string} password
 * @param {{firstName?: string, lastName?: string, companyName?: string}} userDetails
 * @returns {Promise<{data: object|null, error: Error|null}>}
 */
async function performBetterAuthSignUp(email, password, userDetails = {}) {
	if (!userDetails.firstName || !userDetails.lastName || !userDetails.companyName) {
		return { data: null, error: new Error('Fehlende Benutzerdaten für Registrierung') };
	}
	const { data, error } = await authClient.signUp.email({
		email,
		password,
		name: `${userDetails.firstName} ${userDetails.lastName}`,
		// Not a Better Auth field: the backend user-create hook reads it from the raw
		// signup body to fill public.users.companyName and the Pipedrive sync.
		companyName: userDetails.companyName,
		// Without an explicit callbackURL the verification link redirects to `/` and the
		// confirmation page (which triggers org provisioning) is never reached.
		callbackURL: `${window.location.origin}/auth/confirm`,
	});
	if (error) {
		return { data: null, error: new Error(error.message ?? 'Registrierung fehlgeschlagen') };
	}

	// Park the registration details for the confirmation page, which forwards
	// them to complete-signup (org name etc.).
	try {
		localStorage.setItem(
			'pendingSignup',
			JSON.stringify({
				email,
				firstName: userDetails.firstName,
				lastName: userDetails.lastName,
				companyName: userDetails.companyName,
			}),
		);
	} catch {
		// Storage unavailable — the login self-heal provisions the org without the details.
	}

	// Fire the Facebook CompleteRegistration signal.
	const fbEventId = generateEventId();
	const externalId = data?.user?.id ?? null;
	if (typeof window.fbq === 'function') {
		try {
			window.fbq(
				'track',
				'CompleteRegistration',
				{},
				{ eventID: fbEventId, ...(externalId && { external_id: externalId }) },
			);
		} catch {
			// ignored
		}
	}
	await trackCompleteRegistration(
		{ email, firstName: userDetails.firstName, lastName: userDetails.lastName, externalId },
		fbEventId,
	);

	return { data, error: null };
}

export function AuthProvider({ children }) {
	const [session, setSession] = useState(null);
	const [loading, setLoading] = useState(true);

	// Capabilities are a synchronous projection of the JWT — derive them here once per token
	// instead of reading them through an async module cache. This keeps capability checks
	// render-synchronous (no loading flicker) and avoids the token-change effect loops that an
	// async getUserCapabilities() path triggered during signup/token refresh.
	const capabilities = useMemo(() => decodeCapabilities(session?.access_token), [session?.access_token]);

	const { isPlatformAdmin, impersonatedBy } = useMemo(
		() => decodePlatformClaims(session?.access_token),
		[session?.access_token],
	);

	const { data: subscription, refetch: refetchSubscription } = useQuery({
		...subscriptionOptions(),
		enabled: Boolean(session?.user),
	});

	const { data: userProfile, refetch: refetchUserProfile } = useQuery({
		...userProfileOptions(session?.user?.id),
		enabled: Boolean(session?.user),
	});

	const {
		data: orgInfo,
		isPending: orgInfoLoading,
		isError: orgInfoIsError,
		error: orgInfoError,
		refetch: refetchOrgInfo,
	} = useQuery({
		...orgInfoOptions(),
		enabled: Boolean(session?.user),
	});

	const { data: pendingInvites, status: pendingInvitesStatus } = useQuery(
		pendingInvitesOptions(Boolean(session?.user) && !orgInfoLoading && !orgInfo),
	);

	// Self-heal: if a confirmed user has no org (provisioning failed at signup), create it now.
	const { healing } = useEnsureOrg({
		session,
		orgInfo,
		orgInfoLoading,
		orgInfoIsError,
		orgInfoError,
		refetchOrgInfo,
		pendingInvitesStatus,
		pendingInvites,
	});

	/* Load initial session */
	useEffect(() => {
		let isMounted = true;

		/* Better Auth: resolve the current session, then adapt it to the { user, access_token }
		   shape the rest of the app expects (capabilities decode the access_token). */
		(async () => {
			const { data } = await authClient.getSession();
			const access_token = data?.user ? await getAccessToken() : null;
			if (isMounted) {
				setSession(data?.user ? { user: data.user, access_token } : null);
				setLoading(false);
			}
		})();

		return () => {
			isMounted = false;
		};
	}, []);

	useZendeskMessenger(session?.user);

	/* Update Sentry user context when session changes */
	useEffect(() => {
		if (session?.user) {
			// Set Sentry user context on login. Better Auth exposes user.name directly.
			setSentryUser({
				id: session.user.id,
				email: session.user.email,
				name: session.user.name || session.user.email,
			});
		} else {
			// Clear Sentry user context on logout
			clearSentryUser();
		}
	}, [session]);

	/* Refresh the local session from Better Auth (after sign-in). Adapts to the app's session shape. */
	async function refreshBetterAuthSession() {
		const { data } = await authClient.getSession();
		const access_token = data?.user ? await getAccessToken() : null;
		setSession(data?.user ? { user: data.user, access_token } : null);
	}

	/* Auth actions. Better Auth has no onAuthStateChange listener, so sign-in/out update the session
	   state (and clear the query cache) explicitly. The return shape is { data, error } so the
	   calling pages stay unchanged. */
	const signIn = async (email, password) => {
		// A still-valid cached JWT from a previous user on this tab must never carry over.
		clearCachedAuthToken();
		const { data, error } = await authClient.signIn.email({ email, password });
		if (!error) await refreshBetterAuthSession();
		return { data, error };
	};

	const signOut = async () => {
		// Before the session goes: Zendesk keeps its own authenticated session, and the
		// next user on this browser would otherwise inherit the previous chat history.
		logoutZendeskUser();
		const res = await authClient.signOut();
		clearCachedAuthToken();
		setSession(null);
		queryClient.clear();
		return res;
	};

	const resetPassword = (email) =>
		authClient.requestPasswordReset({ email, redirectTo: `${window.location.origin}/reset-password` });

	// The token comes from the ?token= query param the reset link redirects to;
	// Better Auth rejects the call without it (INVALID_TOKEN).
	const updatePassword = (newPassword, token) => authClient.resetPassword({ newPassword, token });

	const resendVerification = (email) =>
		authClient.sendVerificationEmail({ email, callbackURL: `${window.location.origin}/auth/confirm` });

	const signInWithMagicLink = (email) =>
		authClient.signIn.magicLink({ email, callbackURL: `${window.location.origin}/auth/confirm` });

	const value = {
		user: session?.user ?? null,
		session,
		loading,
		capabilities,
		isPlatformAdmin,
		impersonatedBy,
		subscription,
		userProfile,
		termsAcceptedAt: userProfile?.termsAcceptedAt ?? null,
		orgInfo,
		orgRole: orgInfo?.role ?? null,
		orgPlan: orgInfo?.plan ?? null,
		orgSeats: orgInfo?.seats ?? null,
		refetchOrgInfo,
		refetchSubscription,
		refetchUserProfile,
		signIn,
		signInWithMagicLink,
		signUp: performBetterAuthSignUp,
		signOut,
		resetPassword,
		updatePassword,
		resendVerification,
	};

	/* Show loading spinner until session + org data are ready
	   Route guards return null during loading to avoid DOM conflicts with React Router,
	   so we block here instead. Blocking during `healing (creating org if it did not happen
	   during initial signup prevents a freshly-provisioned user from briefly rendering the
	   app before their org/JWT claims are in place. */
	if (loading || (session?.user && (orgInfoLoading || healing))) {
		return <FullscreenLoader />;
	}

	return (
		<AuthContext.Provider value={value}>
			<MaintenanceGate>{children}</MaintenanceGate>
			<PendingInvitesModal invites={pendingInvites ?? []} />
		</AuthContext.Provider>
	);
}

/* Custom hook */
export const useAuth = () => useContext(AuthContext);

AuthProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
